
import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

export interface BulletinVideoPreviewProps {
  src: string;
  isSelected: boolean;
  onLoadedData: () => void;
  onError: () => void;
  isMature?: boolean;
  showMatureContent?: boolean;
}

const BulletinVideoPreview: React.FC<BulletinVideoPreviewProps> = ({
  src,
  isSelected,
  onLoadedData,
  onError,
  isMature = false,
  showMatureContent = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Determine if content should be blurred
  const shouldBlur = isMature && !showMatureContent;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Set up event listeners
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    // Auto-play and loop continuously regardless of selection status
    if (!shouldBlur) {
      const playVideo = async () => {
        try {
          await video.play();
        } catch (error) {
          console.error('Error auto-playing video:', error);
        }
      };
      
      playVideo();
    }
    
    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [shouldBlur]);

  // When selected state changes, handle playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isSelected || !shouldBlur) {
      // Play when selected or when in grid view (and not blurred)
      video.play().catch(error => {
        console.error('Error playing video:', error);
      });
    } else {
      // Pause only if content should be blurred
      video.pause();
    }
  }, [isSelected, shouldBlur]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().catch(error => {
          console.error('Error playing video:', error);
        });
      } else {
        videoRef.current.pause();
      }
    }
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
      <video
        ref={videoRef}
        src={src}
        className={cn(
          "w-full h-full",
          isSelected ? "object-contain" : "object-cover", // Use contain in detail view, cover in grid
          shouldBlur && "blur-xl"
        )}
        loop
        playsInline
        muted={!isSelected} // Only mute in grid view, allow sound in expanded view
        onClick={togglePlay}
        onLoadedData={onLoadedData}
        onError={onError}
      />
      
      {/* Play/Pause button overlay */}
      <div 
        className="absolute inset-0 flex items-center justify-center cursor-pointer z-10"
        onClick={togglePlay}
      >
        <div 
          className={cn(
            "w-12 h-12 rounded-full bg-black/50 flex items-center justify-center transition-opacity",
            isPlaying ? "opacity-0 hover:opacity-100" : "opacity-100"
          )}
        >
          <span className="text-white text-2xl">
            {isPlaying ? "❚❚" : "▶"}
          </span>
        </div>
      </div>
      
      {/* Mature content warning overlay */}
      {shouldBlur && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white p-2 text-center z-20">
          <div>
            <p className="text-sm font-bold">Mature Content</p>
            <p className="text-xs">Enable mature content in settings to view</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulletinVideoPreview;
